import { Box, Grid } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import type { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import type {
  IBuilderTransactionOption,
  ILegalDependencyData,
  ILegalDependencyMockupProps,
  ILegalRequirementDependencyAction,
  ILegalRequirementDependencyCondition,
  ILegalRequirementDependencyConditionGroup,
  ILegalRequirementDependencyRule,
  IRequirementOption,
  IRuleAction,
  IRuleCondition,
  IRuleConditionGroup,
  IRuleSelectionOption,
  PhaseStatus,
  RuleTargetType,
} from '../../Interfaces/ILegalDependency';
import { createEmptyLegalDependencyData } from '../../Interfaces/ILegalDependency';
import { useGetLegalTransactions } from '../../hooks/reactQuerys/LegalTransactionQueries';
import { customFetcher } from '../../hooks/useFetch';
import { LegalDependencyActions } from './LegalDependency/LegalDependencyActions';
import { LegalDependencyConditions } from './LegalDependency/LegalDependencyConditions';
import { LegalDependencyHeader } from './LegalDependency/LegalDependencyHeader';
import { LegalDependencyPreview } from './LegalDependency/LegalDependencyPreview';
import {
  buildRuleNameFromTransactionLabels,
  createAction,
  createCondition,
  createConditionGroup,
  createEmptyBuilderState,
  flattenTargetOptions,
  getActionPhaseId,
  getActionRequirementId,
  getActionRequirementStateId,
  getActionRuleId,
  getActionTransactionId,
  getConditionGroupId,
  getConditionGroupRuleId,
  getConditionPhaseId,
  getConditionRequirementId,
  getConditionRequirementStateId,
  getConditionTransactionId,
  getRuleTransactionId,
  getRuleName,
  getTargetKey,
  getTargetTypeLabel,
  normalizeConditionGroups,
  PHASE_STATUS_OPTIONS,
} from './LegalDependency/utils';

export const LegalDependencyMockup = ({
  currentLegalTransactionId,
  currentLegalPhases,
  legalRequirementStates,
  initialSelectedRuleId = undefined,
  legalDependencyData = createEmptyLegalDependencyData(),
  setLegalDependencyData,
}: ILegalDependencyMockupProps) => {
  const dependencyRuleArray = legalDependencyData.DependencyRuleArray ?? [];
  const dependencyConditionGroupArray =
    legalDependencyData.DependencyConditionGroupArray ?? [];
  const dependencyConditionArray =
    legalDependencyData.DependencyConditionArray ?? [];
  const dependencyActionArray = legalDependencyData.DependencyActionArray ?? [];
  const dependencyLegalTransactionLookup =
    legalDependencyData.DependencyLegalTransactionLookup ?? [];
  const idRef = useRef(0);
  const hasInitializedRuleSelectionRef = useRef(false);
  const loadedRuleIdRef = useRef<number | null>(null);

  const nextId = () => {
    idRef.current += 1;
    return `mock-${idRef.current}`;
  };

  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  const [conditionGroups, setConditionGroups] = useState<IRuleConditionGroup[]>(
    () =>
      createEmptyBuilderState(currentLegalTransactionId ?? null, nextId)
        .conditionGroups
  );
  const [actions, setActions] = useState<IRuleAction[]>(
    () =>
      createEmptyBuilderState(currentLegalTransactionId ?? null, nextId).actions
  );
  const [ruleName, setRuleName] = useState('');
  const [isRuleNameManuallyEdited, setIsRuleNameManuallyEdited] =
    useState(false);
  const { legalTransactions, isLoadingLegalTransactions } =
    useGetLegalTransactions();

  const filteredRules = useMemo(
    () =>
      dependencyRuleArray
        .filter(
          rule =>
            currentLegalTransactionId === undefined ||
            getRuleTransactionId(rule) === currentLegalTransactionId
        )
        .sort((a, b) => a.idLRDRule - b.idLRDRule),
    [currentLegalTransactionId, dependencyRuleArray]
  );

  const groupsByRuleId = useMemo(() => {
    const result = new Map<
      number,
      ILegalRequirementDependencyConditionGroup[]
    >();

    dependencyConditionGroupArray.forEach(group => {
      const ruleId = getConditionGroupRuleId(group);

      if (ruleId === null) {
        return;
      }

      const groups = result.get(ruleId) || [];
      groups.push(group);
      result.set(ruleId, groups);
    });

    result.forEach(groups => groups.sort((a, b) => a.Position - b.Position));

    return result;
  }, [dependencyConditionGroupArray]);

  const conditionsByGroupId = useMemo(() => {
    const result = new Map<number, ILegalRequirementDependencyCondition[]>();

    dependencyConditionArray.forEach(condition => {
      const groupId = getConditionGroupId(condition);

      if (groupId === null) {
        return;
      }

      const conditions = result.get(groupId) || [];
      conditions.push(condition);
      result.set(groupId, conditions);
    });

    result.forEach(conditions =>
      conditions.sort((a, b) => a.Position - b.Position)
    );

    return result;
  }, [dependencyConditionArray]);

  const actionsByRuleId = useMemo(() => {
    const result = new Map<number, ILegalRequirementDependencyAction[]>();

    dependencyActionArray.forEach(action => {
      const ruleId = getActionRuleId(action);

      if (ruleId === null) {
        return;
      }

      const actionsForRule = result.get(ruleId) || [];
      actionsForRule.push(action);
      result.set(ruleId, actionsForRule);
    });

    result.forEach(actionsForRule =>
      actionsForRule.sort((a, b) => a.Position - b.Position)
    );

    return result;
  }, [dependencyActionArray]);

  const rulesById = useMemo(
    () => new Map(dependencyRuleArray.map(rule => [rule.idLRDRule, rule])),
    [dependencyRuleArray]
  );

  const isDependenciesLoading = false;
  const isSavePending = false;
  const isDeletePending = false;

  const isActionToolbarBusy =
    isDependenciesLoading || isSavePending || isDeletePending;

  const transactionMap = useMemo(() => {
    const transactionEntries: Array<
      [number, { idLegalTransaction: number; Title?: string | null }]
    > = [
      ...dependencyLegalTransactionLookup.map(
        transaction =>
          [transaction.idLegalTransaction, transaction] as [
            number,
            { idLegalTransaction: number; Title?: string | null },
          ]
      ),
      ...(legalTransactions || []).map(
        legalTransaction =>
          [
            legalTransaction.idLegalTransaction,
            {
              idLegalTransaction: legalTransaction.idLegalTransaction,
              Title: legalTransaction.Title,
            },
          ] as [number, { idLegalTransaction: number; Title?: string | null }]
      ),
    ];

    return new Map<
      number,
      { idLegalTransaction: number; Title?: string | null }
    >(transactionEntries);
  }, [dependencyLegalTransactionLookup, legalTransactions]);

  const builderTransactionOptions = useMemo<IBuilderTransactionOption[]>(() => {
    const lookupTransactions = dependencyLegalTransactionLookup
      .filter(
        lookup =>
          !(legalTransactions || []).some(
            legalTransaction =>
              legalTransaction.idLegalTransaction === lookup.idLegalTransaction
          )
      )
      .map(lookup => ({
        idLegalTransaction: lookup.idLegalTransaction,
        Title: lookup.Title ?? null,
      }));

    return [...(legalTransactions || []), ...lookupTransactions].map(
      legalTransaction => ({
        value: legalTransaction.idLegalTransaction,
        label:
          legalTransaction.Title && legalTransaction.Title.trim().length > 0
            ? legalTransaction.Title
            : `Vorgang #${legalTransaction.idLegalTransaction}`,
      })
    );
  }, [dependencyLegalTransactionLookup, legalTransactions]);

  const selectedTransactionIds = useMemo(() => {
    const transactionIds = new Set<number>();

    conditionGroups.forEach(group => {
      group.conditions.forEach(condition => {
        if (condition.transactionId !== null) {
          transactionIds.add(condition.transactionId);
        }
      });
    });

    actions.forEach(action => {
      if (action.transactionId !== null) {
        transactionIds.add(action.transactionId);
      }
    });

    return Array.from(transactionIds);
  }, [actions, conditionGroups]);

  const selectedTransactionQueries = useQueries({
    queries: selectedTransactionIds.map(transactionId => ({
      queryKey: ['legaltransaction/fullObject', transactionId],
      queryFn: () =>
        customFetcher({
          endpoint: `/legaltransaction/fullObject/${transactionId}`,
        }) as Promise<ILegalTransactionFullObject>,
      enabled: !(
        currentLegalTransactionId === transactionId &&
        currentLegalPhases !== undefined
      ),
      refetchOnWindowFocus: false,
    })),
  });

  const transactionPhasesById = useMemo(() => {
    const result = new Map<number, ILegalPhaseFullObject[]>();

    selectedTransactionIds.forEach((transactionId, index) => {
      if (
        currentLegalTransactionId === transactionId &&
        currentLegalPhases !== undefined
      ) {
        result.set(transactionId, currentLegalPhases);
        return;
      }

      result.set(
        transactionId,
        selectedTransactionQueries[index].data?.LegalPhaseArray || []
      );
    });

    return result;
  }, [
    currentLegalPhases,
    currentLegalTransactionId,
    selectedTransactionIds,
    selectedTransactionQueries,
  ]);

  const isTransactionLoading = (transactionId: number | null) => {
    if (transactionId === null) {
      return false;
    }

    if (
      currentLegalTransactionId === transactionId &&
      currentLegalPhases !== undefined
    ) {
      return false;
    }

    const transactionIndex = selectedTransactionIds.indexOf(transactionId);

    return transactionIndex >= 0
      ? selectedTransactionQueries[transactionIndex].isFetching
      : false;
  };

  const requirementOptionsByTransaction = useMemo(() => {
    const result = new Map<number, IRequirementOption[]>();

    selectedTransactionIds.forEach(transactionId => {
      result.set(
        transactionId,
        flattenTargetOptions(
          transactionPhasesById.get(transactionId) || [],
          transactionId,
          transactionMap.get(transactionId)?.Title?.trim()
            ? transactionMap.get(transactionId)?.Title ||
                `Vorgang #${transactionId}`
            : `Vorgang #${transactionId}`,
          legalRequirementStates
        )
      );
    });

    return result;
  }, [
    legalRequirementStates,
    selectedTransactionIds,
    transactionMap,
    transactionPhasesById,
  ]);

  const requirementLookup = useMemo(() => {
    const result = new Map<string, IRequirementOption>();

    requirementOptionsByTransaction.forEach((options, transactionId) => {
      options.forEach(option => {
        const targetId =
          option.targetType === 'requirement'
            ? option.idLegalRequirement
            : option.idLegalPhase;

        if (targetId === null) {
          return;
        }

        result.set(
          getTargetKey(transactionId, option.targetType, targetId),
          option
        );
      });
    });

    return result;
  }, [requirementOptionsByTransaction]);

  const legalRequirementStateMap = useMemo(
    () =>
      new Map(
        legalRequirementStates.map(state => [
          state.idLegalRequirementState,
          state.LegalRequirementState,
        ])
      ),
    [legalRequirementStates]
  );

  const getStatusLabel = (
    targetType: RuleTargetType,
    requirementStateId: number | null,
    phaseIsCompleted: PhaseStatus | null
  ) => {
    if (targetType === 'phase') {
      return phaseIsCompleted === null
        ? null
        : PHASE_STATUS_OPTIONS.find(option => option.value === phaseIsCompleted)
            ?.label || null;
    }

    return requirementStateId !== null
      ? legalRequirementStateMap.get(requirementStateId) || null
      : null;
  };

  const getTransactionLabelsForBuilderState = (
    groups: IRuleConditionGroup[],
    nextActions: IRuleAction[]
  ) => {
    const transactionIds: number[] = [];
    const seenTransactionIds = new Set<number>();

    const addTransactionId = (transactionId: number | null) => {
      if (transactionId === null || seenTransactionIds.has(transactionId)) {
        return;
      }

      seenTransactionIds.add(transactionId);
      transactionIds.push(transactionId);
    };

    groups.forEach(group => {
      group.conditions.forEach(condition =>
        addTransactionId(condition.transactionId)
      );
    });
    nextActions.forEach(action => addTransactionId(action.transactionId));

    return transactionIds.map(transactionId =>
      transactionMap.get(transactionId)?.Title?.trim()
        ? transactionMap.get(transactionId)?.Title ||
          `Vorgang #${transactionId}`
        : `Vorgang #${transactionId}`
    );
  };

  const getSuggestedRuleName = (
    groups: IRuleConditionGroup[],
    nextActions: IRuleAction[],
    ruleId: number | null
  ) => {
    const labels = getTransactionLabelsForBuilderState(groups, nextActions);
    if (labels.length > 1) {
      return buildRuleNameFromTransactionLabels(labels);
    }

    const getTargetLabel = (
      transactionId: number | null,
      targetType: RuleTargetType,
      idReq: number | null,
      idPhase: number | null
    ): string | null => {
      if (transactionId === null) return null;
      const targetId = targetType === 'requirement' ? idReq : idPhase;
      if (targetId === null) return null;
      const option = requirementLookup.get(
        getTargetKey(transactionId, targetType, targetId)
      );
      return option?.legalTargetLabel?.trim() || null;
    };

    const firstWord = (label: string) => label.split(' ')[0];

    const conditionLabels: string[] = [];
    groups.forEach(group => {
      group.conditions.forEach(condition => {
        const label = getTargetLabel(
          condition.transactionId,
          condition.targetType,
          condition.idLegalRequirement,
          condition.idLegalPhase
        );
        if (label) conditionLabels.push(firstWord(label));
      });
    });

    const actionLabels: string[] = [];
    nextActions.forEach(action => {
      const label = getTargetLabel(
        action.transactionId,
        action.targetType,
        action.idLegalRequirement,
        action.idLegalPhase
      );
      if (label) actionLabels.push(firstWord(label));
    });

    const allTransactionIds = [
      ...groups.flatMap(g => g.conditions.map(c => c.transactionId)),
      ...nextActions.map(a => a.transactionId),
    ].filter((id): id is number => id !== null);
    const transactionId = allTransactionIds[0] ?? null;

    const prefix = [
      ruleId !== null ? `#${ruleId}` : null,
      transactionId !== null ? `#${transactionId}` : null,
    ]
      .filter(Boolean)
      .join(' ');

    const formatLabels = (arr: string[]) => {
      if (arr.length === 0) return null;
      if (arr.length <= 2) return arr.join(', ');
      return `${arr[0]}, ${arr[1]} +${arr.length - 2}`;
    };

    const whenPart = formatLabels(conditionLabels);
    const thenPart = formatLabels(actionLabels);

    const namePart =
      whenPart && thenPart
        ? `${whenPart} → ${thenPart}`
        : whenPart || thenPart || labels[0] || '';

    return [prefix, namePart].filter(Boolean).join(' ');
  };

  const getPersistedGroupsForRule = (ruleId: number): IRuleConditionGroup[] =>
    normalizeConditionGroups(
      (groupsByRuleId.get(ruleId) || []).map((group, groupIndex) => ({
        id: `selection-group-${group.idLRDConditionGroup}-${groupIndex}`,
        operator:
          groupIndex === 0 ? null : group.LogicalOperatorToPrevious || 'UND',
        conditions: (
          conditionsByGroupId.get(group.idLRDConditionGroup) || []
        ).map(condition => ({
          id: `selection-condition-${condition.idLRDCondition}`,
          transactionId: getConditionTransactionId(condition),
          targetType: condition.TargetType,
          idLegalRequirement:
            condition.TargetType === 'requirement'
              ? getConditionRequirementId(condition)
              : null,
          idLegalPhase:
            condition.TargetType === 'phase'
              ? getConditionPhaseId(condition)
              : null,
          idLegalRequirementState:
            condition.TargetType === 'requirement'
              ? getConditionRequirementStateId(condition)
              : null,
          PhaseIsCompleted:
            condition.TargetType === 'phase'
              ? condition.PhaseIsCompleted === null ||
                condition.PhaseIsCompleted === undefined
                ? null
                : ((condition.PhaseIsCompleted ? 1 : 0) as PhaseStatus)
              : null,
        })),
      }))
    );

  const getPersistedActionsForRule = (ruleId: number): IRuleAction[] =>
    (actionsByRuleId.get(ruleId) || []).map(action => ({
      id: `selection-action-${action.idLRDAction}`,
      transactionId: getActionTransactionId(action),
      targetType: action.TargetType,
      idLegalRequirement:
        action.TargetType === 'requirement'
          ? getActionRequirementId(action)
          : null,
      idLegalPhase:
        action.TargetType === 'phase' ? getActionPhaseId(action) : null,
      targetStateId:
        action.TargetType === 'requirement'
          ? getActionRequirementStateId(action)
          : null,
      TargetPhaseIsCompleted:
        action.TargetType === 'phase'
          ? action.TargetPhaseIsCompleted === null ||
            action.TargetPhaseIsCompleted === undefined
            ? null
            : ((action.TargetPhaseIsCompleted ? 1 : 0) as PhaseStatus)
          : null,
    }));

  const suggestedRuleName = useMemo(
    () => getSuggestedRuleName(conditionGroups, actions, selectedRuleId),
    [actions, conditionGroups, selectedRuleId, transactionMap]
  );

  const ruleSelectionOptions = useMemo<IRuleSelectionOption[]>(
    () =>
      filteredRules.map(rule => {
        const storedRuleName = getRuleName(rule);
        const persistedGroups = getPersistedGroupsForRule(rule.idLRDRule);
        const persistedActions = getPersistedActionsForRule(rule.idLRDRule);

        return {
          id: rule.idLRDRule,
          label:
            storedRuleName ||
            buildRuleNameFromTransactionLabels(
              getTransactionLabelsForBuilderState(
                persistedGroups,
                persistedActions
              ),
              `Regel #${rule.idLRDRule}`
            ),
        };
      }),
    [
      actionsByRuleId,
      conditionsByGroupId,
      filteredRules,
      groupsByRuleId,
      transactionMap,
    ]
  );

  const updateConditionGroup = (
    groupId: string,
    updates: Partial<IRuleConditionGroup>
  ) => {
    setConditionGroups(currentGroups =>
      normalizeConditionGroups(
        currentGroups.map(group =>
          group.id === groupId ? { ...group, ...updates } : group
        )
      )
    );
  };

  const updateCondition = (
    groupId: string,
    conditionId: string,
    updates: Partial<IRuleCondition>
  ) => {
    setConditionGroups(currentGroups =>
      normalizeConditionGroups(
        currentGroups.map(group =>
          group.id === groupId
            ? {
                ...group,
                conditions: group.conditions.map(condition =>
                  condition.id === conditionId
                    ? { ...condition, ...updates }
                    : condition
                ),
              }
            : group
        )
      )
    );
  };

  const updateAction = (actionId: string, updates: Partial<IRuleAction>) => {
    setActions(currentActions =>
      currentActions.map(action =>
        action.id === actionId ? { ...action, ...updates } : action
      )
    );
  };

  const resetBuilder = () => {
    const emptyState = createEmptyBuilderState(
      currentLegalTransactionId ?? null,
      nextId
    );
    hasInitializedRuleSelectionRef.current = true;
    loadedRuleIdRef.current = null;
    setSelectedRuleId(null);
    setIsRuleNameManuallyEdited(false);
    setConditionGroups(emptyState.conditionGroups);
    setActions(emptyState.actions);
  };

  const loadRuleIntoBuilder = (ruleId: number | null) => {
    if (ruleId === null) {
      const emptyState = createEmptyBuilderState(
        currentLegalTransactionId ?? null,
        nextId
      );
      loadedRuleIdRef.current = null;
      setIsRuleNameManuallyEdited(false);
      setConditionGroups(emptyState.conditionGroups);
      setActions(emptyState.actions);
      return;
    }

    const rule = rulesById.get(ruleId);
    const groups = groupsByRuleId.get(ruleId) || [];
    const mappedGroups: IRuleConditionGroup[] = normalizeConditionGroups(
      groups.map((group, groupIndex) => ({
        id: `group-${group.idLRDConditionGroup}-${nextId()}`,
        idLRDConditionGroup: group.idLRDConditionGroup,
        operator:
          groupIndex === 0 ? null : group.LogicalOperatorToPrevious || 'UND',
        conditions: (
          conditionsByGroupId.get(group.idLRDConditionGroup) || []
        ).map(condition => ({
          id: `condition-${condition.idLRDCondition}-${nextId()}`,
          idLRDCondition: condition.idLRDCondition,
          transactionId: getConditionTransactionId(condition),
          targetType: condition.TargetType,
          idLegalRequirement:
            condition.TargetType === 'requirement'
              ? getConditionRequirementId(condition)
              : null,
          idLegalPhase:
            condition.TargetType === 'phase'
              ? getConditionPhaseId(condition)
              : null,
          idLegalRequirementState:
            condition.TargetType === 'requirement'
              ? getConditionRequirementStateId(condition)
              : null,
          PhaseIsCompleted:
            condition.TargetType === 'phase'
              ? condition.PhaseIsCompleted === null ||
                condition.PhaseIsCompleted === undefined
                ? null
                : ((condition.PhaseIsCompleted ? 1 : 0) as PhaseStatus)
              : null,
        })),
      }))
    );
    const mappedActions: IRuleAction[] = (
      actionsByRuleId.get(ruleId) || []
    ).map(action => ({
      id: `action-${action.idLRDAction}-${nextId()}`,
      idLRDAction: action.idLRDAction,
      transactionId: getActionTransactionId(action),
      targetType: action.TargetType,
      idLegalRequirement:
        action.TargetType === 'requirement'
          ? getActionRequirementId(action)
          : null,
      idLegalPhase:
        action.TargetType === 'phase' ? getActionPhaseId(action) : null,
      targetStateId:
        action.TargetType === 'requirement'
          ? getActionRequirementStateId(action)
          : null,
      TargetPhaseIsCompleted:
        action.TargetType === 'phase'
          ? action.TargetPhaseIsCompleted === null ||
            action.TargetPhaseIsCompleted === undefined
            ? null
            : ((action.TargetPhaseIsCompleted ? 1 : 0) as PhaseStatus)
          : null,
    }));

    loadedRuleIdRef.current = ruleId;
    const storedRuleName = getRuleName(rule);

    setRuleName(
      storedRuleName ||
        getSuggestedRuleName(
          mappedGroups,
          mappedActions,
          rule?.idLRDRule ?? null
        )
    );
    setIsRuleNameManuallyEdited(storedRuleName !== null);
    setConditionGroups(
      mappedGroups.length > 0
        ? mappedGroups
        : createEmptyBuilderState(currentLegalTransactionId ?? null, nextId)
            .conditionGroups
    );
    setActions(
      mappedActions.length > 0
        ? mappedActions
        : createEmptyBuilderState(currentLegalTransactionId ?? null, nextId)
            .actions
    );
  };

  const getNextTemporaryId = (ids: number[]) =>
    ids.length === 0
      ? -1
      : Math.min(...ids.filter(id => Number.isFinite(id)), 0) - 1;

  const isConditionComplete = (condition: IRuleCondition) => {
    if (condition.transactionId === null) {
      return false;
    }

    if (condition.targetType === 'requirement') {
      return (
        condition.idLegalRequirement !== null &&
        condition.idLegalRequirementState !== null
      );
    }

    return (
      condition.idLegalPhase !== null && condition.PhaseIsCompleted !== null
    );
  };

  const isActionComplete = (action: IRuleAction) => {
    if (action.transactionId === null) {
      return false;
    }

    if (action.targetType === 'requirement') {
      return (
        action.idLegalRequirement !== null && action.targetStateId !== null
      );
    }

    return (
      action.idLegalPhase !== null && action.TargetPhaseIsCompleted !== null
    );
  };

  const canSaveRule = useMemo(
    () =>
      conditionGroups.some(group =>
        group.conditions.some(condition => isConditionComplete(condition))
      ) && actions.some(action => isActionComplete(action)),
    [actions, conditionGroups]
  );

  const handleSaveRule = async () => {
    const transactionId = currentLegalTransactionId ?? null;

    if (transactionId === null || !setLegalDependencyData) {
      return;
    }

    const persistedRuleId =
      selectedRuleId ??
      getNextTemporaryId(dependencyRuleArray.map(rule => rule.idLRDRule));

    const persistedRule = rulesById.get(persistedRuleId);
    const nextRule: ILegalRequirementDependencyRule = persistedRule
      ? {
          ...persistedRule,
          LegalTransaction_idLegalTransaction: transactionId,
          idLegalTransaction: transactionId,
          Name: ruleName,
        }
      : {
          idLRDRule: persistedRuleId,
          LegalTransaction_idLegalTransaction: transactionId,
          idLegalTransaction: transactionId,
          Name: ruleName,
          Executed_at: null,
        };

    let nextGroupId = getNextTemporaryId(
      dependencyConditionGroupArray.map(group => group.idLRDConditionGroup)
    );
    let nextConditionId = getNextTemporaryId(
      dependencyConditionArray.map(condition => condition.idLRDCondition)
    );
    let nextActionId = getNextTemporaryId(
      dependencyActionArray.map(action => action.idLRDAction)
    );

    const nextGroups: ILegalRequirementDependencyConditionGroup[] = [];
    const nextConditions: ILegalRequirementDependencyCondition[] = [];
    const nextActions: ILegalRequirementDependencyAction[] = actions.map(
      (action, actionIndex) => {
        const actionId = action.idLRDAction ?? nextActionId;

        if (action.idLRDAction === undefined) {
          nextActionId -= 1;
        }

        return {
          idLRDAction: actionId,
          LRDRule_idLRDRule: persistedRuleId,
          idLRDRule: persistedRuleId,
          Position: actionIndex + 1,
          LegalTransaction_idLegalTransaction:
            action.transactionId ?? transactionId,
          idLegalTransaction: action.transactionId ?? transactionId,
          TargetType: action.targetType,
          LegalRequirement_idLegalRequirement:
            action.targetType === 'requirement'
              ? action.idLegalRequirement
              : null,
          idLegalRequirement:
            action.targetType === 'requirement'
              ? action.idLegalRequirement
              : null,
          TargetLegalRequirementState_idLegalRequirementState:
            action.targetType === 'requirement' ? action.targetStateId : null,
          idLegalRequirementState:
            action.targetType === 'requirement' ? action.targetStateId : null,
          LegalPhase_idLegalPhase:
            action.targetType === 'phase' ? action.idLegalPhase : null,
          idLegalPhase:
            action.targetType === 'phase' ? action.idLegalPhase : null,
          TargetPhaseIsCompleted:
            action.targetType === 'phase'
              ? action.TargetPhaseIsCompleted === null
                ? null
                : action.TargetPhaseIsCompleted === 1
              : null,
        };
      }
    );

    conditionGroups.forEach((group, groupIndex) => {
      const groupId = group.idLRDConditionGroup ?? nextGroupId;

      if (group.idLRDConditionGroup === undefined) {
        nextGroupId -= 1;
      }

      nextGroups.push({
        idLRDConditionGroup: groupId,
        LRDRule_idLRDRule: persistedRuleId,
        idLRDRule: persistedRuleId,
        Position: groupIndex + 1,
        LogicalOperatorToPrevious:
          groupIndex === 0 ? null : group.operator || 'UND',
      });

      group.conditions.forEach((condition, conditionIndex) => {
        const conditionId = condition.idLRDCondition ?? nextConditionId;

        if (condition.idLRDCondition === undefined) {
          nextConditionId -= 1;
        }

        nextConditions.push({
          idLRDCondition: conditionId,
          LRDConditionGroup_idLRDConditionGroup: groupId,
          idLRDConditionGroup: groupId,
          Position: conditionIndex + 1,
          LegalTransaction_idLegalTransaction:
            condition.transactionId ?? transactionId,
          idLegalTransaction: condition.transactionId ?? transactionId,
          TargetType: condition.targetType,
          LegalRequirement_idLegalRequirement:
            condition.targetType === 'requirement'
              ? condition.idLegalRequirement
              : null,
          idLegalRequirement:
            condition.targetType === 'requirement'
              ? condition.idLegalRequirement
              : null,
          LegalRequirementState_idLegalRequirementState:
            condition.targetType === 'requirement'
              ? condition.idLegalRequirementState
              : null,
          idLegalRequirementState:
            condition.targetType === 'requirement'
              ? condition.idLegalRequirementState
              : null,
          LegalPhase_idLegalPhase:
            condition.targetType === 'phase' ? condition.idLegalPhase : null,
          idLegalPhase:
            condition.targetType === 'phase' ? condition.idLegalPhase : null,
          PhaseIsCompleted:
            condition.targetType === 'phase'
              ? condition.PhaseIsCompleted === null
                ? null
                : condition.PhaseIsCompleted === 1
              : null,
        });
      });
    });

    setLegalDependencyData(currentData => {
      const currentDependencyData =
        currentData ?? createEmptyLegalDependencyData();
      const currentRules = currentDependencyData.DependencyRuleArray ?? [];
      const currentGroups =
        currentDependencyData.DependencyConditionGroupArray ?? [];
      const currentConditions =
        currentDependencyData.DependencyConditionArray ?? [];
      const currentActions = currentDependencyData.DependencyActionArray ?? [];
      const removedGroupIds = new Set(
        currentGroups
          .filter(group => getConditionGroupRuleId(group) === persistedRuleId)
          .map(group => group.idLRDConditionGroup)
      );

      return {
        ...currentDependencyData,
        DependencyRuleArray: [
          ...currentRules.filter(rule => rule.idLRDRule !== persistedRuleId),
          nextRule,
        ].sort((a, b) => a.idLRDRule - b.idLRDRule),
        DependencyConditionGroupArray: [
          ...currentGroups.filter(
            group => getConditionGroupRuleId(group) !== persistedRuleId
          ),
          ...nextGroups,
        ],
        DependencyConditionArray: [
          ...currentConditions.filter(condition => {
            const groupId = getConditionGroupId(condition);
            return groupId === null || !removedGroupIds.has(groupId);
          }),
          ...nextConditions,
        ],
        DependencyActionArray: [
          ...currentActions.filter(
            action => getActionRuleId(action) !== persistedRuleId
          ),
          ...nextActions,
        ],
      } as ILegalDependencyData;
    });

    loadedRuleIdRef.current = persistedRuleId;
    setSelectedRuleId(persistedRuleId);
  };

  const handleDeleteRule = async () => {
    if (selectedRuleId === null) {
      resetBuilder();
      return;
    }

    if (!setLegalDependencyData) {
      return;
    }

    const removedGroupIds = new Set(
      dependencyConditionGroupArray
        .filter(group => getConditionGroupRuleId(group) === selectedRuleId)
        .map(group => group.idLRDConditionGroup)
    );

    setLegalDependencyData(currentData => {
      const currentDependencyData =
        currentData ?? createEmptyLegalDependencyData();

      return {
        ...currentDependencyData,
        DependencyRuleArray: (
          currentDependencyData.DependencyRuleArray ?? []
        ).filter(rule => rule.idLRDRule !== selectedRuleId),
        DependencyConditionGroupArray: (
          currentDependencyData.DependencyConditionGroupArray ?? []
        ).filter(group => getConditionGroupRuleId(group) !== selectedRuleId),
        DependencyConditionArray: (
          currentDependencyData.DependencyConditionArray ?? []
        ).filter(condition => {
          const groupId = getConditionGroupId(condition);
          return groupId === null || !removedGroupIds.has(groupId);
        }),
        DependencyActionArray: (
          currentDependencyData.DependencyActionArray ?? []
        ).filter(action => getActionRuleId(action) !== selectedRuleId),
      };
    });
    resetBuilder();
  };

  useEffect(() => {
    hasInitializedRuleSelectionRef.current = false;
    loadedRuleIdRef.current = null;
  }, [currentLegalTransactionId, initialSelectedRuleId]);

  useEffect(() => {
    if (!isRuleNameManuallyEdited) {
      setRuleName(suggestedRuleName);
    }
  }, [isRuleNameManuallyEdited, suggestedRuleName]);

  useEffect(() => {
    if (isDependenciesLoading || hasInitializedRuleSelectionRef.current) {
      return;
    }

    if (initialSelectedRuleId === undefined) {
      return;
    }

    // null means "open fresh — skip auto-selection"
    if (initialSelectedRuleId === null) {
      hasInitializedRuleSelectionRef.current = true;
      return;
    }

    if (!filteredRules.some(rule => rule.idLRDRule === initialSelectedRuleId)) {
      return;
    }

    hasInitializedRuleSelectionRef.current = true;
    loadedRuleIdRef.current = null;
    setSelectedRuleId(currentSelectedRuleId =>
      currentSelectedRuleId === initialSelectedRuleId
        ? currentSelectedRuleId
        : initialSelectedRuleId
    );
  }, [filteredRules, initialSelectedRuleId, isDependenciesLoading]);

  useEffect(() => {
    if (isDependenciesLoading || hasInitializedRuleSelectionRef.current) {
      return;
    }

    hasInitializedRuleSelectionRef.current = true;

    if (filteredRules.length > 0) {
      setSelectedRuleId(filteredRules[0].idLRDRule);
    }
  }, [filteredRules, isDependenciesLoading]);

  useEffect(() => {
    if (selectedRuleId === null) {
      return;
    }

    if (!filteredRules.some(rule => rule.idLRDRule === selectedRuleId)) {
      resetBuilder();
      return;
    }

    if (isDependenciesLoading || loadedRuleIdRef.current === selectedRuleId) {
      return;
    }

    loadRuleIntoBuilder(selectedRuleId);
  }, [filteredRules, isDependenciesLoading, selectedRuleId]);

  const getRequirementSummary = (
    transactionId: number | null,
    targetType: RuleTargetType,
    requirementId: number | null,
    phaseId: number | null,
    stateId: number | null,
    phaseIsCompleted: PhaseStatus | null
  ) => {
    const targetId = targetType === 'requirement' ? requirementId : phaseId;

    if (transactionId === null || targetId === null) {
      return 'Unvollständig';
    }

    const requirement = requirementLookup.get(
      getTargetKey(transactionId, targetType, targetId)
    );
    const transactionLabel =
      builderTransactionOptions.find(option => option.value === transactionId)
        ?.label || `Vorgang #${transactionId}`;
    const targetTypeLabel = getTargetTypeLabel(targetType);

    if (!requirement) {
      return `${transactionLabel} / ${targetTypeLabel} #${targetId}`;
    }

    const statusLabel = getStatusLabel(targetType, stateId, phaseIsCompleted);

    if (statusLabel === null) {
      return `${transactionLabel} / ${targetTypeLabel}: ${requirement.legalTargetLabel}`;
    }

    return `${transactionLabel} / ${targetTypeLabel}: ${requirement.legalTargetLabel} = ${statusLabel}`;
  };

  const getTransactionOption = (transactionId: number | null) =>
    builderTransactionOptions.find(option => option.value === transactionId) ||
    null;

  const getRequirementOption = (
    transactionId: number | null,
    targetType: RuleTargetType,
    requirementId: number | null,
    phaseId: number | null
  ) => {
    const targetId = targetType === 'requirement' ? requirementId : phaseId;

    if (transactionId === null || targetId === null) {
      return null;
    }

    return (
      requirementOptionsByTransaction
        .get(transactionId)
        ?.find(
          option =>
            option.targetType === targetType &&
            (targetType === 'requirement'
              ? option.idLegalRequirement === requirementId
              : option.idLegalPhase === phaseId)
        ) || null
    );
  };

  const handleRuleSelectionChange = (ruleId: number | null) => {
    loadedRuleIdRef.current = null;

    if (ruleId === null) {
      resetBuilder();
      return;
    }

    setSelectedRuleId(ruleId);
  };

  const handleResetToCurrentRule = () => {
    loadedRuleIdRef.current = null;
    setIsRuleNameManuallyEdited(false);
    loadRuleIntoBuilder(selectedRuleId);
  };

  const handleRuleNameChange = (nextRuleName: string) => {
    setRuleName(nextRuleName);
    setIsRuleNameManuallyEdited(true);
  };

  const handleAddConditionGroup = () => {
    setConditionGroups(currentGroups =>
      normalizeConditionGroups([
        ...currentGroups,
        createConditionGroup(
          nextId(),
          createCondition(nextId(), currentLegalTransactionId ?? null),
          'UND'
        ),
      ])
    );
  };

  const handleAddAction = () => {
    setActions(currentActions => [
      ...currentActions,
      createAction(nextId(), currentLegalTransactionId ?? null),
    ]);
  };

  const handleAddCondition = (groupId: string) => {
    setConditionGroups(currentGroups =>
      normalizeConditionGroups(
        currentGroups.map(group =>
          group.id !== groupId
            ? group
            : {
                ...group,
                conditions: [
                  ...group.conditions,
                  createCondition(nextId(), currentLegalTransactionId ?? null),
                ],
              }
        )
      )
    );
  };

  const handleRemoveCondition = (groupId: string, conditionId: string) => {
    setConditionGroups(currentGroups =>
      normalizeConditionGroups(
        currentGroups.map(group =>
          group.id !== groupId
            ? group
            : {
                ...group,
                conditions:
                  group.conditions.length === 1
                    ? group.conditions
                    : group.conditions.filter(
                        condition => condition.id !== conditionId
                      ),
              }
        )
      )
    );
  };

  const handleRemoveGroup = (groupId: string) => {
    setConditionGroups(currentGroups =>
      currentGroups.length === 1
        ? currentGroups
        : normalizeConditionGroups(
            currentGroups.filter(group => group.id !== groupId)
          )
    );
  };

  const handleRemoveAction = (actionId: string) => {
    setActions(currentActions =>
      currentActions.length === 1
        ? currentActions
        : currentActions.filter(action => action.id !== actionId)
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <LegalDependencyHeader
            canSaveRule={canSaveRule}
            isActionToolbarBusy={isActionToolbarBusy}
            isDeletePending={isDeletePending}
            isDependenciesLoading={isDependenciesLoading}
            isSavePending={isSavePending}
            onAddAction={handleAddAction}
            onAddConditionGroup={handleAddConditionGroup}
            onDeleteRule={handleDeleteRule}
            onResetBuilder={resetBuilder}
            onResetToCurrentRule={handleResetToCurrentRule}
            onRuleNameChange={handleRuleNameChange}
            onRuleSelectionChange={handleRuleSelectionChange}
            onSaveRule={handleSaveRule}
            ruleName={ruleName}
            ruleSelectionOptions={ruleSelectionOptions}
            selectedRuleId={selectedRuleId}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LegalDependencyConditions
            conditionGroups={conditionGroups}
            getRequirementOption={getRequirementOption}
            getStatusLabel={getStatusLabel}
            getTransactionOption={getTransactionOption}
            isLoadingLegalTransactions={isLoadingLegalTransactions}
            isTransactionLoading={isTransactionLoading}
            legalRequirementStates={legalRequirementStates}
            onAddCondition={handleAddCondition}
            onChangeCondition={updateCondition}
            onChangeGroupOperator={(groupId, operator) =>
              updateConditionGroup(groupId, { operator })
            }
            onRemoveCondition={handleRemoveCondition}
            onRemoveGroup={handleRemoveGroup}
            requirementOptionsByTransaction={requirementOptionsByTransaction}
            transactionOptions={builderTransactionOptions}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <LegalDependencyActions
            actions={actions}
            getRequirementOption={getRequirementOption}
            getStatusLabel={getStatusLabel}
            getTransactionOption={getTransactionOption}
            isLoadingLegalTransactions={isLoadingLegalTransactions}
            isTransactionLoading={isTransactionLoading}
            legalRequirementStates={legalRequirementStates}
            onChangeAction={updateAction}
            onRemoveAction={handleRemoveAction}
            requirementOptionsByTransaction={requirementOptionsByTransaction}
            transactionOptions={builderTransactionOptions}
          />
        </Grid>

        <Grid item xs={12}>
          <LegalDependencyPreview
            actions={actions}
            conditionGroups={conditionGroups}
            getRequirementSummary={getRequirementSummary}
            getStatusLabel={getStatusLabel}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
